import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config';
import './styles.css';
// import { toast } from 'react-hot-toast';

interface SettingsProps {
  onBack: () => void;
}

const Settings: React.FC<SettingsProps> = ({ onBack }) => {
  const { t } = useTranslation('settings');
  const { currentLanguage, changeLanguage } = useLanguage();
  const { user, updateProfile, token } = useAuth();
  const [nickname, setNickname] = useState(user?.nickname || '');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [storageUsage, setStorageUsage] = useState<{
    used: number;
    total: number;
  }>({ used: 0, total: 100 }); // 100MB for regular users

  useEffect(() => {
    const fetchStorageUsage = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/storage/usage`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Storage usage error:', errorData);
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Storage usage data:', data);
        
        setStorageUsage({
          used: data.used,
          total: data.total || 100
        });
      } catch (error) {
        console.error('Failed to fetch storage usage:', error);
        setMessage({ 
          text: t('storage.fetch_error'), 
          type: 'error' 
        });
      }
    };

    if (token) {
      fetchStorageUsage();
    }
  }, [t, token]);

  useEffect(() => {
    const resizeObserverTimeout = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 1000);

    return () => clearTimeout(resizeObserverTimeout);
  }, []);

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    changeLanguage(e.target.value);
  };

  const handleSaveNickname = async () => {
    setIsSaving(true);
    try {
      await updateProfile({ nickname });
      setMessage({ text: t('profile.save_success'), type: 'success' });
      setTimeout(() => setMessage(null), 2000);
    } catch (error) {
      setMessage({ text: t('profile.save_error'), type: 'error' });
      setTimeout(() => setMessage(null), 2000);
    } finally {
      setIsSaving(false);
    }
  };

  const getStorageBarColor = () => {
    const usagePercentage = (storageUsage.used / storageUsage.total) * 100;
    if (usagePercentage >= 90) return '#ff4d4f'; // 红色警告
    if (usagePercentage >= 70) return '#faad14'; // 黄色警告
    return '#4096ff'; // 正常蓝色
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={20} />
          <span>{t('back')}</span>
        </button>
        <h2>{t('title')}</h2>
      </div>

      {/* Profile Section */}
      <section className="settings-section">
        <h3>{t('profile.title')}</h3>
        <div className="settings-form">
          <div className="form-group">
            <label>{t('profile.nickname')}</label>
            <div className="input-with-button">
              <input 
                type="text" 
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder={t('profile.nickname_placeholder')} 
              />
              <button 
                className="save-button"
                onClick={handleSaveNickname}
                disabled={isSaving}
              >
                {isSaving ? t('profile.saving') : t('profile.save')}
              </button>
            </div>
            {message && (
              <div className={`message ${message.type}`}>
                {message.text}
              </div>
            )}
          </div>
          <div className="form-group">
            <label>{t('profile.email')}</label>
            <input 
              type="email" 
              value={user?.email || ''}
              placeholder={t('profile.email_placeholder') as string} 
              disabled 
            />
          </div>
        </div>
      </section>

      {/* Preferences Section */}
      <section className="settings-section">
        <h3>{t('preferences.title')}</h3>
        <div className="settings-form">
          <div className="form-group">
            <label>{t('preferences.theme')}</label>
            <select>
              <option value="light">{t('preferences.theme_light')}</option>
              <option value="dark">{t('preferences.theme_dark')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('preferences.language')}</label>
            <select value={currentLanguage} onChange={handleLanguageChange}>
              <option value="zh">{t('preferences.language_zh')}</option>
              <option value="en">{t('preferences.language_en')}</option>
              <option value="fr">{t('preferences.language_fr')}</option>
            </select>
          </div>
        </div>
      </section>

      {/* Storage Section */}
      <section className="settings-section">
        <h3>{t('storage.title')}</h3>
        <div className="settings-form">
          <div className="form-group">
            <label>{t('storage.usage')}</label>
            <div className="storage-bar">
              <div 
                className="storage-progress" 
                style={{ 
                  width: `${Math.min((storageUsage.used / storageUsage.total) * 100, 100)}%`,
                  backgroundColor: getStorageBarColor()
                }}
              />
            </div>
            <div className="storage-info">
              {t('storage.usage_details', {
                used: storageUsage.used.toFixed(1),
                total: storageUsage.total
              })}
              {storageUsage.used >= storageUsage.total * 0.9 && (
                <span className="storage-warning">
                  {t('storage.warning_limit')}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Account Section */}
      <section className="settings-section">
        <h3>{t('account.title')}</h3>
        <div className="settings-form">
          <div className="form-group">
            <label>{t('account.change_password')}</label>
            <button className="secondary-button">
              {t('account.change_password')}
            </button>
          </div>
          <div className="form-group">
            <label>{t('account.notifications')}</label>
            <div className="toggle-group">
              <label className="toggle">
                <input type="checkbox" />
                <span>{t('account.email_notifications')}</span>
              </label>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Settings; 